/**
 * OrderDevicesPage — the hardware-order coupon-fulfillment queue/ledger.
 * Replaces the "Invoice for Ordering Device Status" Google Sheet. Gated to the
 * troubleshoot tier (root/engineer/support); backend re-checks via AdminRoleGuard.
 *
 * Tabs: Active queue (paid, awaiting coupon) · All orders (full ledger) ·
 * Unpaid coupons (school net-terms watchlist). Search spans email / coupon /
 * stripe id / invoice id. The query lives here (not in a child) so "Refresh
 * usage" can scope the Shopify check to the on-screen rows.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@staff-app/ui';
import { RefreshCw, Search } from 'lucide-react';
import type { OrderDeviceTab } from '@staff-app/shared-types';
import { ORDER_PAGE_SIZE, useOrderDeviceTasks, useSyncCouponUsage } from '../hooks/useOrderDeviceTasks';
import { OrderTaskTable } from '../components/OrderTaskTable';
import { MintedCouponDialog, type MintedCoupon } from '../components/MintedCouponDialog';

const TABS: { value: OrderDeviceTab; label: string }[] = [
  { value: 'ACTIVE', label: 'Active queue' },
  { value: 'ALL', label: 'All orders' },
  { value: 'UNPAID_COUPONS', label: 'Unpaid coupons' },
];

// Stripe's five invoice statuses, verbatim. The server canonicalises both sides of this
// filter, so picking `open` also returns the backfilled rows recorded as UNPAID or PAST_DUE —
// one entry per real state, instead of one per historical spelling.
const STATUS_OPTIONS = ['draft', 'open', 'paid', 'uncollectible', 'void'];
const ALL = '__all__';

export function OrderDevicesPage() {
  const [tab, setTab] = useState<OrderDeviceTab>('ACTIVE');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [couponUsed, setCouponUsed] = useState<string>('');
  // Owned HERE, not by the row's mint button: minting gives the task a coupon, so the table
  // re-renders that row as "Issued" and unmounts the button — a dialog held there would be
  // destroyed before it could show. See MintedCouponDialog.
  const [minted, setMinted] = useState<MintedCoupon | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  // Any change to what is being LISTED invalidates the page number — page 4 of the unpaid tab is
  // not page 4 of a search for "nucor", and landing on an out-of-range offset shows an empty
  // table that reads like "no results" rather than "you are past the end".
  useEffect(() => {
    setPageIndex(0);
  }, [tab, search, status, couponUsed]);

  const { data, loading, error, refetch } = useOrderDeviceTasks(
    tab,
    search,
    status,
    couponUsed,
    pageIndex * ORDER_PAGE_SIZE,
  );
  const page = data?.deviceOrderTasks;
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * ORDER_PAGE_SIZE + 1;
  const lastShown = Math.min(total, pageIndex * ORDER_PAGE_SIZE + items.length);
  const hasPrev = pageIndex > 0;
  const hasNext = lastShown < total;

  const [syncUsage, { loading: syncing }] = useSyncCouponUsage();
  const refreshUsage = async () => {
    // Scope the Shopify check to the rows currently on screen (not the whole
    // ~1.7k-row collection). The 15-min background job does the full sweep.
    await syncUsage({ variables: { invoiceIds: items.map((t) => t._id) } });
    await refetch();
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-hidden p-6" data-testid="order-devices-page">
      {/* No heading here: the active tab already names this section, and the shell owns the
          page title. Only the description earns its place. */}
      <p className="text-sm text-muted-foreground">
        {'Hardware-order coupon fulfillment. Paid orders await a Shopify code; issue it and mark the order closed.'}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, coupon code, stripe id, invoice id…"
            className="pl-9"
            data-testid="order-search"
          />
        </div>

        <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? '' : v)}>
          <SelectTrigger className="w-[190px]" data-testid="filter-status">
            <SelectValue placeholder="Invoice status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All statuses'}</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={couponUsed || ALL} onValueChange={(v) => setCouponUsed(v === ALL ? '' : v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-coupon-used">
            <SelectValue placeholder="Coupon used" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'Coupon: all'}</SelectItem>
            <SelectItem value="USED">{'Coupon used'}</SelectItem>
            <SelectItem value="UNUSED">{'Coupon unused'}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          className="gap-1"
          disabled={syncing || items.length === 0}
          onClick={() => { void refreshUsage(); }}
          data-testid="order-refresh-usage"
          title="Pull the latest orders and check Shopify for coupon redemptions"
        >
          <RefreshCw className={`h-4 w-4${syncing ? ' animate-spin' : ''}`} />
          {syncing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as OrderDeviceTab)} className="flex min-h-0 flex-1 flex-col">
        <TabsList className="self-start">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className="cursor-pointer data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-lg data-[state=active]:shadow-primary/50"
              data-testid={`order-tab-${t.value}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-4 flex min-h-0 flex-1 flex-col gap-2">
          {error ? (
            <div className="rounded-md border border-destructive p-4 text-sm text-destructive">{error.message}</div>
          ) : (
            <>
              <div className="shrink-0 text-xs text-muted-foreground" data-testid="order-count">
                {loading && !page
                  ? 'Loading…'
                  : total > items.length
                    ? `${firstShown}–${lastShown} of ${total} orders`
                    : `${total} order${total === 1 ? '' : 's'}`}
              </div>
              <OrderTaskTable tasks={items} tab={tab} onMinted={setMinted} />
              {/* Only when there is more than one page — a pager under a 12-row list is noise. */}
              {total > ORDER_PAGE_SIZE && (
                <div className="flex shrink-0 items-center justify-end gap-2 pt-1">
                  <span className="text-xs text-muted-foreground" data-testid="order-page-label">
                    {`Page ${pageIndex + 1} of ${Math.max(1, Math.ceil(total / ORDER_PAGE_SIZE))}`}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasPrev || loading}
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    data-testid="order-page-prev"
                  >
                    {'Previous'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasNext || loading}
                    onClick={() => setPageIndex((p) => p + 1)}
                    data-testid="order-page-next"
                  >
                    {'Next'}
                  </Button>
                </div>
              )}
              <MintedCouponDialog minted={minted} onClose={() => setMinted(null)} />
            </>
          )}
        </div>
      </Tabs>
    </div>
  );
}
